import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { postReportRequestAtom } from '@/interfaces/site';
import { tempUnitAtom } from '@/interfaces/utils';
import { snackbarDataAtom } from '@/components/common/ReportSnackbar';
import { subscribeReportGenerate } from '@/interfaces/report';

import type { ReportRequestData } from '@/@types/site';

interface ExportDialogProps {
  onClose: (reason?: string) => void;
}

export default function ExportDialog({ onClose }: ExportDialogProps) {
  const { name: deviceName } = useAtomValue(selectedDeviceSafeAtom);
  const tempUnit = useAtomValue(tempUnitAtom);
  const { mutate: request, data: jobId, isPending } = useAtomValue(postReportRequestAtom);
  const setSnackbarData = useSetAtom(snackbarDataAtom);

  const [unit, setUnit] = useState<ReportRequestData['unit']>(tempUnit);
  const [format, setFormat] = useState('pdf');
  const { t } = useTranslation();

  useEffect(() => {
    if (jobId === undefined) return;
    if (jobId) {
      const onComplete = () => {
        setSnackbarData({ open: true, isFDA: format === 'fda', deviceName, error: false });
      };
      const onError = () => {
        setSnackbarData({ open: true, isFDA: false, deviceName, error: true });
      };
      window.alert(t('Devices.message.requestSuccess'));
      subscribeReportGenerate(jobId, onComplete, onError);
      onClose();
    } else {
      window.alert(t('Devices.message.requestFail'));
    }
  }, [deviceName, format, jobId, onClose, setSnackbarData, t]);

  function onDownloadClick() {
    request({ unit, format });
  }

  return (
    <Stack gap={2} sx={{ minWidth: 480, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('Devices.label.export')}</DialogTitle>
      <Typography sx={{ mb: -2 }}>{t('Devices.label.temperatureUnit')}</Typography>
      <RadioGroup row value={unit} onChange={(_, value) => setUnit(value as ReportRequestData['unit'])}>
        <FormControlLabel value="CELSIUS" control={<Radio />} label="℃" />
        <FormControlLabel value="FAHRENHEIT" control={<Radio />} label="℉" />
      </RadioGroup>
      <Typography sx={{ mb: -2 }}>{t('Devices.label.fileFormat')}</Typography>
      <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value)}>
        <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
        <FormControlLabel value="csv" control={<Radio />} label="CSV" />
        <FormControlLabel value="fda" control={<Radio />} label="FDA" />
      </RadioGroup>

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={() => onClose()} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button variant="contained" disabled={isPending} onClick={onDownloadClick} sx={{ bgcolor: 'info.main' }}>
          {isPending ? <CircularProgress size={24} color="inherit" /> : t('Devices.label.download')}
        </Button>
      </Stack>
    </Stack>
  );
}
